import { AbstractControl, ValidationErrors, ValidatorFn } from "@angular/forms";
import { CallingCodeVM } from "../models";

export interface ParsedMobileNumber {
  callingCode: CallingCodeVM;
  localNumber: string;
}

const DEFAULT_CALLING_CODE = 44;
const allowedLocalNumberPattern = /^[0-9\s().-]+$/;

export function parseMobileNumber(value: string, callingCodes: CallingCodeVM[], fallbackCallingCode?: CallingCodeVM): ParsedMobileNumber {
  const defaultCallingCode = fallbackCallingCode || findCallingCode(callingCodes, DEFAULT_CALLING_CODE) || firstCallingCode(callingCodes);

  if (!value) {
    return {
      callingCode: defaultCallingCode,
      localNumber: ''
    };
  }

  const trimmedValue = value.trim();
  const hasInternationalPrefix = /^\s*(\+|00)/.test(trimmedValue);
  let digits = onlyDigits(trimmedValue);

  if (/^\s*00/.test(trimmedValue)) {
    digits = digits.replace(/^00/, '');
  }

  if (digits && (hasInternationalPrefix || !digits.startsWith('0'))) {
    const parsedCallingCode = findCallingCodePrefix(callingCodes, digits);

    if (parsedCallingCode != null) {
      return {
        callingCode: parsedCallingCode,
        localNumber: digits.substring(parsedCallingCode.callingCode.toString().length)
      };
    }
  }

  return {
    callingCode: defaultCallingCode,
    localNumber: trimmedValue
  };
}

export function formatMobileNumber(callingCode: CallingCodeVM, localNumber: string): string {
  if (callingCode == null || callingCode.callingCode == null || !localNumber) {
    return '';
  }

  const subscriberNumber = normaliseSubscriberNumber(localNumber);

  if (!subscriberNumber) {
    return '';
  }

  return '+' + callingCode.callingCode + ' ' + subscriberNumber;
}

export function mobileNumberValidator(getCallingCode: () => CallingCodeVM): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    const callingCode = getCallingCode();

    if (callingCode == null || callingCode.callingCode == null) {
      return { callingCodeRequired: true };
    }

    const value = control.value.toString().trim();

    if (!allowedLocalNumberPattern.test(value)) {
      return { mobileNumber: true };
    }

    const localDigits = onlyDigits(value);
    const subscriberNumber = normaliseSubscriberNumber(value);
    const totalDigits = callingCode.callingCode.toString().length + subscriberNumber.length;

    if (!localDigits || subscriberNumber.length < 4 || totalDigits < 8 || totalDigits > 15) {
      return { mobileNumber: true };
    }

    if (callingCode.callingCode === 44 && !/^7\d{9}$/.test(subscriberNumber)) {
      return { mobileNumber: true };
    }

    if (callingCode.callingCode === 1 && !/^[2-9]\d{2}[2-9]\d{6}$/.test(subscriberNumber)) {
      return { mobileNumber: true };
    }

    return null;
  };
}

function findCallingCode(callingCodes: CallingCodeVM[], callingCode: number): CallingCodeVM {
  if (!callingCodes) {
    return null;
  }

  return callingCodes.find(x => x.callingCode === callingCode) || null;
}

function findCallingCodePrefix(callingCodes: CallingCodeVM[], digits: string): CallingCodeVM {
  if (!callingCodes) {
    return null;
  }

  const matchingCodes = callingCodes
    .filter(x => x.callingCode != null && digits.startsWith(x.callingCode.toString()))
    .sort((a, b) => b.callingCode.toString().length - a.callingCode.toString().length);

  return matchingCodes.length > 0 ? matchingCodes[0] : null;
}

function firstCallingCode(callingCodes: CallingCodeVM[]): CallingCodeVM {
  return callingCodes && callingCodes.length > 0 ? callingCodes[0] : null;
}

function normaliseSubscriberNumber(value: string): string {
  return onlyDigits(value).replace(/^0+/, '');
}

function onlyDigits(value: string): string {
  return value ? value.replace(/\D/g, '') : '';
}
